import { collectCollectionUnlock, collectFinishGuide } from "@/utils/event"
import { actRequest, basicRequest, type RESP } from "./request"

export const completeShare = async (type: "main" | "recall" = "main") => {
    return actRequest.post<null, RESP<null>>(
        `/api/activities/${__ACTIVITY_CID}/share`,
        { type },
    )
}

interface CollectionItem {
    key: string
    ts: number
}

export const unlockCollection = async (
    gameStartTs: number,
    group: string,
    targets: string,
) => {
    if (group === "system" && targets === "guide") {
        collectFinishGuide()
    } else {
        collectCollectionUnlock(gameStartTs, targets)
    }

    return actRequest.post<
        null,
        RESP<{
            result: boolean
            list: CollectionItem[]
        }>
    >(`/api/activities/${__ACTIVITY_CID}/collection`, { group, targets })
}

export const getCollection = async (group?: string) => {
    return actRequest.get<null, RESP<Record<string, CollectionItem[] | null>>>(
        `/api/activities/${__ACTIVITY_CID}/collection`,
        { params: { group } },
    )
}

export type CouponItem = {
    cid: string
    name: string
    rarity: number
    status: number
    reset: boolean
    result: {
        giftName: string
        prizeId: string
        prizeType: "GOODS" | "GIFT" | "MISS"
        won: boolean
    } | null
}

export type DrawInfo = {
    coupons: CouponItem[]
    availableDrawCountMap: Record<string, number>
    contactInfo: Record<string, object | null>
}

export const getDrawInfo = async () => {
    return actRequest.get<null, RESP<DrawInfo>>(
        `/api/activities/${__ACTIVITY_CID}/draw`,
    )
}

export type DrawResult = {
    giftName: string
    prizeId: string
    prizeType: "GOODS" | "GIFT" | "MISS"
    won: boolean
}

export const postDraw = async (coupon: string) => {
    return actRequest.post<null, RESP<DrawResult>>(
        `/api/activities/${__ACTIVITY_CID}/draw`,
        { coupon },
    )
}

export type ContactInfo = {
    name: string
    phone: string
    email: string
    postcode: string
    address: string
    province?: string
    city?: string
    county?: string
}

export const postDrawContact = async (coupon: string, contact: ContactInfo) => {
    return actRequest.post<null, RESP<null>>(
        `/api/activities/${__ACTIVITY_CID}/draw/contact`,
        { coupon, ...contact },
    )
}

export type TaskStatusItem = {
    cid: string
    status: 0 | 10 | 20
    progress: { value: number } | null
}

export const getTaskStatus = async () => {
    return actRequest.post<null, RESP<TaskStatusItem[]>>(
        `/api/activities/${__ACTIVITY_CID}/task`,
    )
}

export type ActivityStatusDto = {
    avatar: string
    isRecallUser: boolean
}

export const getActivityStatus = async () => {
    return actRequest.get<null, RESP<ActivityStatusDto>>(
        "/api/activities/footprints/status",
    )
}

export const completeTask = async (taskCid: string) => {
    return actRequest.post<null, RESP<null>>(
        `/api/activities/${__ACTIVITY_CID}/task/${taskCid}/complete`,
    )
}

export type TeamAvatarDto = {
    type: string
    id: string
    url: string
}

export type TeamMemberDto = {
    uid: string
    nickname: string | null
    avatar: TeamAvatarDto | null
}

export type OwnTeamDto = {
    code: string
    captain: TeamMemberDto
    member: TeamMemberDto | null
    /** @deprecated */ captainUid: string
    /** @deprecated */ memberUid: string | null
    /** @deprecated */ captainNickname: string | null
    /** @deprecated */ memberNickname: string | null
}

export type SearchTeamDto = {
    code: string
    captain: TeamMemberDto
    /** @deprecated */ captainUid: string
    /** @deprecated */ captainNickname: string | null
}

export const searchTeam = async (code: string) => {
    return actRequest.get<null, RESP<SearchTeamDto>>(
        `/api/activities/${__ACTIVITY_CID}/team/search`,
        { params: { code } },
    )
}

export const syncTeam = async () => {
    return actRequest.post<null, RESP<OwnTeamDto>>(
        `/api/activities/${__ACTIVITY_CID}/team/sync`,
    )
}

export const getJoinedTeam = async () => {
    return actRequest.get<null, RESP<OwnTeamDto | null>>(
        `/api/activities/${__ACTIVITY_CID}/team/joined`,
    )
}

export const joinTeam = async (code: string) => {
    return actRequest.post<null, RESP<{}>>(
        `/api/activities/${__ACTIVITY_CID}/team/join`,
        { code },
    )
}

export const getMeta = async () => {
    return basicRequest.get<
        null,
        RESP<{
            percent: number
            letter?: { to: string; contents: string[]; signature: string }
        }>
    >("/api/activity/meta")
}

export type ActivityTask = {
    cid: string
    order: number
    template: string
    templateParams?: Record<string, string>
}

export type ActivityMeta = {
    cid: string
    game: string
    template: string
    tasks: ActivityTask[]
}

export const getActivityMeta = async () => {
    return actRequest.get<null, RESP<ActivityMeta>>(
        "/api/activities/footprints/meta",
    )
}

export type WebcmsProgressItem = {
    id: number
    title: string
    weight: number
    link: string
    percent: number
}

export const getWebcmsProgress = async () => {
    return actRequest.get<
        null,
        RESP<{ results: { rows: WebcmsProgressItem[] } }>
    >(`/api/activities/${__ACTIVITY_CID}/webcms`, {
        params: { key: "complete", size: 1 },
    })
}
